import {
  getActiveSession,
  GetContactInformationMyMSC,
  updateActiveSession,
} from '@msc-b2c/shared-utils';

/**
 * Module-level set of goldenIds for which a GetContactInformationMyMSC call is
 * already in progress or has been initiated by another component (e.g. the customer
 * information container). Used to prevent duplicate API calls when multiple
 * components on the same page would independently trigger enrichment.
 */
const _fetchGuard = new Set<string>();

/**
 * Signals that a GetContactInformationMyMSC call for the given goldenId has
 * already been initiated (e.g. by the customer information hooks).
 * Call this BEFORE firing the API so that enrichSessionWithContactInfo skips.
 */
export const markContactInfoFetchStarted = (goldenId: string): void => {
  _fetchGuard.add(goldenId);
};

/**
 * Fires a non-blocking, fire-and-forget call to GetContactInformationMyMSC
 * to enrich the active full-login session with gender and mobilePhone.
 *
 * Guards:
 * - Skipped when gender is already present in the session (already enriched).
 * - Skipped when another component has already initiated the same API call
 *   (see markContactInfoFetchStarted).
 *
 * Should be called on the first My MSC page that loads after full login.
 */
export const enrichSessionWithContactInfo = (goldenId: string): void => {
  if (getActiveSession().gender) return;
  if (_fetchGuard.has(goldenId)) return;

  markContactInfoFetchStarted(goldenId);

  new GetContactInformationMyMSC(false, { goldenId })
    .getData()
    .then((info) => {
      if (info.gender) updateActiveSession('gender', info.gender);
      if (info.mobilePhone) updateActiveSession('mobilePhone', info.mobilePhone);
    })
    .catch(() => {
      // Allow retry on a future page load.
      _fetchGuard.delete(goldenId);
    });
};
